import type { ReactNode } from "react";
import { ClassActivityCategory, classActivityColors } from "../../constants/ClassActivityConstants";
import useBreakpoints from "../../hooks/useBreakpoints";

interface ClassMemberActivityCardShellProps {
    category: ClassActivityCategory;
    /** Receives the badge icon size class for the active breakpoint. */
    renderBadgeIcon: (iconSizeClass: string) => ReactNode;
    categoryLabel: string;
    name: string;
    subInfo?: ReactNode;
    action?: ReactNode;
    /**
     * Passive action content (e.g. a status indicator) is vertically centered next to the text
     * column on desktop; interactive buttons pin to the top so they stay put as the card grows.
     */
    isActionCentered?: boolean;
    "data-testid"?: string;
    "data-status"?: string;
}

/**
 * The concrete cards supply content and behavior through the slots, so design adjustments to the
 * card shape (frame, breakpoint layouts, icon badge, text column) only ever happen here.
 */
export const ClassMemberActivityCardShell = (props: ClassMemberActivityCardShellProps) => {
    const { isAboveMD } = useBreakpoints();
    const colors = classActivityColors[props.category];

    const shellClass = `w-full bg-white border ${colors.border} border-l-8 rounded-medium`;

    const renderIconBadge = (iconSize: string, borderWidth: string, padding: string) => (
        <div className={`${borderWidth} ${colors.border} rounded-pill ${padding} flex items-center shrink-0`}>
            {props.renderBadgeIcon(iconSize)}
        </div>
    );

    const renderCategoryLabel = () => (
        <p className={`text-l font-semibold ${colors.text} uppercase`}>{props.categoryLabel}</p>
    );

    const renderName = () => <h3 className="text-xl font-semibold text-p6-grey-dark break-words">{props.name}</h3>;

    // Mobile: icon + label on one row, then name and sub info, action right-aligned below.
    if (!isAboveMD) {
        return (
            <div
                className={`${shellClass} flex flex-col gap-m p-m`}
                data-testid={props["data-testid"]}
                data-status={props["data-status"]}
            >
                <div className="flex flex-col gap-xs">
                    <div className="flex items-center gap-s">
                        {renderIconBadge("text-xl", "border", "p-xs")}
                        {renderCategoryLabel()}
                    </div>
                    {renderName()}
                    {props.subInfo}
                </div>
                {props.action && <div className="flex justify-end w-full">{props.action}</div>}
            </div>
        );
    }

    // Desktop: icon badge left, text column, action right.
    return (
        <div
            className={`${shellClass} flex items-center gap-m pl-l pr-m py-m`}
            data-testid={props["data-testid"]}
            data-status={props["data-status"]}
        >
            {renderIconBadge("text-icon-m", "border-2", "p-s")}
            <div className="flex flex-1 min-w-[0px] flex-col gap-xs">
                <div className="flex flex-col gap-xxs">
                    {renderCategoryLabel()}
                    {renderName()}
                </div>
                {props.subInfo}
            </div>
            {props.action && (
                <div className={`shrink-0 ${props.isActionCentered ? "" : "self-start"}`}>{props.action}</div>
            )}
        </div>
    );
};
